import apiClient from './client';

export interface ActivityActor {
  type: 'user' | 'system' | 'vendor';
  id?: string;
  displayName?: string;
}

export interface ActivitySource {
  system: string;
  version?: string;
}

export interface Activity {
  id: string;
  urn: string;
  lenderId: string;
  occurredAt: string;
  recordedAt: string;
  type: string;
  actor: ActivityActor;
  source: ActivitySource;
  title: string;
  description: string;
  attributes?: Record<string, unknown>;
  channel: 'web' | 'api' | 'batch' | 'vendor-webhook' | 'kafka';
  severity: 'info' | 'warning' | 'critical';
  schemaVersion: number;
  traceId?: string;
}

export interface ActivityListParams {
  cursor?: string;
  limit?: number;
  from?: string;
  to?: string;
  type?: string;
  severity?: string;
}

export interface ActivityListResponse {
  items: Activity[];
  nextCursor?: string;
}

export interface ActivitySummary {
  totalCount: number;
  countsByType: Record<string, number>;
  countsBySeverity: Record<string, number>;
}

/** Extract UUID from a URN like urn:oms:order:UUID, or return as-is if already a UUID */
function extractOrderId(orderUrn: string): string {
  if (orderUrn.startsWith('urn:oms:order:')) {
    return orderUrn.replace('urn:oms:order:', '');
  }
  // fallback: take last segment after colon
  const parts = orderUrn.split(':');
  return parts[parts.length - 1];
}

export async function getActivities(
  orderUrn: string,
  params?: ActivityListParams,
): Promise<ActivityListResponse> {
  const orderId = extractOrderId(orderUrn);
  // Build clean params — avoid sending limit as string (backend validates @IsInt)
  const cleanParams: Record<string, string> = {};
  if (params?.cursor) cleanParams.cursor = params.cursor;
  if (params?.from) cleanParams.from = params.from;
  if (params?.to) cleanParams.to = params.to;
  if (params?.type) cleanParams.type = params.type;
  if (params?.severity) cleanParams.severity = params.severity;

  const response = await apiClient.get(`/oms/activity-log/${orderId}/activity`, { params: cleanParams });
  return response.data;
}

export async function getActivitySummary(orderUrn: string): Promise<ActivitySummary> {
  const orderId = extractOrderId(orderUrn);
  const response = await apiClient.get(`/oms/activity-log/${orderId}/activity/summary`);
  return response.data;
}
